import {
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    FormControl,
    FormLabel,
    PopoverTrigger,
    Switch,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { AbsencePeriod, AbsenceType, User } from '../../types';
import { useCreateAbsence, useUpdateAbsence } from '../../query/resource-hooks/absence';
import { AbsenceRecord } from '../../types';
import { useSuToast } from '../../hooks/useSuToast';
import SchemaProvider from '../../utils/SchemaProvider';
import EmployeeSelectButton from '../ui/EmployeeSelectButton';
import { RangeDatepicker } from '../calendarPicker';
import FormikTextInput from '../formik/FormikTextInput';
import { CheckIcon } from '@chakra-ui/icons';
import { TimeRangeVisualizer } from '../common/DateRangeVisualizer';
import { useRef } from 'react';
import { useSuIntl } from '../../hooks/useSuIntl';
import { DateTime } from 'luxon';
import { AbsenceTypePicker } from './AbsenceTypePicker';

type CreateAbsenceDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    absenceRecord?: AbsenceRecord;
    initialUser?: User;
};

export const CreateAbsenceDrawer: React.FC<CreateAbsenceDrawerProps> = ({ isOpen, onClose, onSuccess, absenceRecord, initialUser }) => {
    const intl = useIntl();
    const { t, formatDate } = useSuIntl();
    const toast = useSuToast();
    const schema = SchemaProvider.getAbsenceSchema(intl);
    const drawerContentRef = useRef(null);
    const isEditMode = !!absenceRecord;

    const createAbsence = useCreateAbsence(
        () => {
            toast.success(intl.formatMessage({ id: 'absence.created' }));
            onSuccess();
            handleClose();
        },
        () => {
            toast.error(intl.formatMessage({ id: 'absence.error.create' }));
        }
    );

    const updateAbsence = useUpdateAbsence(
        () => {
            toast.success(intl.formatMessage({ id: 'absence.updated' }));
            onSuccess();
            handleClose();
        },
        () => {
            toast.error(intl.formatMessage({ id: 'absence.error.update' }));
        }
    );

    function handleClose() {
        createAbsence.reset();
        updateAbsence.reset();
        onClose();
    }

    const isLoading = createAbsence.isLoading || updateAbsence.isLoading;

    return (
        <Drawer size={'md'} isOpen={isOpen} onClose={handleClose}>
            <DrawerOverlay />
            <DrawerContent m={{ sm: 0, md: 2 }} borderRadius={'xl'}>
                <DrawerHeader borderBottomWidth="1px">
                    {intl.formatMessage({ id: isEditMode ? 'absence.update' : 'absence.create' })}
                </DrawerHeader>
                <DrawerCloseButton />
                <DrawerBody ref={drawerContentRef}>
                    <Formik
                        validationSchema={schema.createAbsence}
                        initialValues={{
                            userId: absenceRecord?.userId as number | undefined,
                            type: absenceRecord?.type ?? AbsenceType.VACATION,
                            dateFrom: absenceRecord?.dateFrom ?? new Date().toISOString(),
                            dateTo: absenceRecord?.dateTo ?? new Date().toISOString(),
                            period: absenceRecord?.period ?? AbsencePeriod.FULL_DAY,
                            note: absenceRecord?.note ?? '',
                        }}
                        enableReinitialize
                        onSubmit={(values) => {
                            const period = values.period as AbsencePeriod;
                            if (isEditMode) {
                                updateAbsence.mutate({
                                    id: absenceRecord.id,
                                    data: {
                                        type: values.type as AbsenceType,
                                        dateFrom: DateTime.fromISO(values.dateFrom).toFormat('yyyy-MM-dd'),
                                        dateTo: DateTime.fromISO(values.dateTo).toFormat('yyyy-MM-dd'),
                                        period,
                                        note: values.note || undefined,
                                    },
                                });
                            } else {
                                createAbsence.mutate({
                                    type: values.type as AbsenceType,
                                    dateFrom: DateTime.fromISO(values.dateFrom).toFormat('yyyy-MM-dd'),
                                    dateTo: DateTime.fromISO(values.dateTo).toFormat('yyyy-MM-dd'),
                                    period,
                                    note: values.note || undefined,
                                    userId: values.userId,
                                });
                            }
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, setFieldValue }) => (
                            <Form>
                                <VStack spacing={4}>
                                    {!isEditMode && (
                                        <Box width={'100%'}>
                                            <EmployeeSelectButton
                                                onSelect={(user) => {
                                                    setFieldValue('userId', user.id);
                                                }}
                                                initialUser={initialUser}
                                            />
                                            {!!(errors.userId && touched.userId) && (
                                                <Text color="red.500" fontSize="sm">
                                                    {errors.userId as string}
                                                </Text>
                                            )}
                                        </Box>
                                    )}

                                    <AbsenceTypePicker
                                        value={values.type as AbsenceType}
                                        onChange={(type) => setFieldValue('type', type)}
                                        label={t('type')}
                                    />

                                    <FormControl>
                                        <FormLabel>{t('selectDate')}</FormLabel>
                                        <Box flex={1} justifyItems={'center'}>
                                            <RangeDatepicker
                                                selectedDates={[
                                                    ...(values.dateFrom ? [new Date(values.dateFrom)] : []),
                                                    ...(values.dateTo ? [new Date(values.dateTo)] : []),
                                                ]}
                                                onDateChange={(dates) => {
                                                    setFieldValue('dateFrom', dates[0]?.toISOString() ?? '');
                                                    setFieldValue('dateTo', dates[1]?.toISOString() ?? '');
                                                    if (dates[0] && dates[1] && dates[0].toDateString() !== dates[1].toDateString()) {
                                                        setFieldValue('period', AbsencePeriod.FULL_DAY);
                                                    }
                                                }}
                                                usePortal
                                                portalRef={drawerContentRef}
                                            >
                                                {(dates) => (
                                                    <PopoverTrigger>
                                                        <Box>
                                                            <TimeRangeVisualizer
                                                                interactive
                                                                orientation={'row'}
                                                                cursor={'pointer'}
                                                                dateFrom={dates?.[0] ? formatDate(dates[0]) : t('dateFrom')}
                                                                dateTo={dates?.[1] ? formatDate(dates[1]) : t('dateTo')}
                                                            />
                                                        </Box>
                                                    </PopoverTrigger>
                                                )}
                                            </RangeDatepicker>
                                        </Box>
                                    </FormControl>

                                    {values.dateFrom &&
                                        values.dateTo &&
                                        DateTime.fromISO(values.dateFrom).hasSame(DateTime.fromISO(values.dateTo), 'day') && (
                                            <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                                <FormLabel mb={0}>{intl.formatMessage({ id: 'absence.halfDay' })}</FormLabel>
                                                <Switch
                                                    colorScheme="primary"
                                                    isChecked={values.period === AbsencePeriod.HALF_DAY}
                                                    onChange={(e) =>
                                                        setFieldValue(
                                                            'period',
                                                            e.target.checked ? AbsencePeriod.HALF_DAY : AbsencePeriod.FULL_DAY
                                                        )
                                                    }
                                                />
                                            </FormControl>
                                        )}

                                    <FormikTextInput
                                        errors={errors}
                                        touched={touched}
                                        name="note"
                                        fieldName="note"
                                        handleBlur={handleBlur}
                                        handleChange={handleChange}
                                        label={intl.formatMessage({ id: 'note' })}
                                        values={values}
                                    />

                                    <Button isLoading={isLoading} colorScheme="green" rightIcon={<CheckIcon />} type="submit" width="100%">
                                        {intl.formatMessage({ id: isEditMode ? 'absence.update' : 'form.submit' })}
                                    </Button>
                                </VStack>
                            </Form>
                        )}
                    </Formik>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};
